import { Reducer } from 'redux';
import { update } from '../helpers/immutabilityHelper'
import * as Dtos from '../dtos/Tmd.dtos';

import {
    ACTION_INSTITUTIONS_LOAD,
    ACTION_INSTITUTIONS_LOAD_BY_ID,
    ACTION_INSTITUTIONS_LOAD_FAILURE,
    ACTION_INSTITUTIONS_LOAD_SUCCESS,

    ACTION_INSTITUTIONS_SAVE,
    ACTION_INSTITUTIONS_SAVE_SUCCESS,
    ACTION_INSTITUTIONS_SAVE_FAILURE,

    ACTION_INSTITUTIONS_CREATE,
    ACTION_INSTITUTIONS_CREATE_SUCCESS,
    ACTION_INSTITUTIONS_CREATE_FAILURE,

    ACTION_INSTITUTIONS_VALIDATE,
    ACTION_INSTITUTIONS_VALIDATE_SUCCESS,
    ACTION_INSTITUTIONS_VALIDATE_FAILURE,

    ACTION_INSTITUTIONS_CLEAR,
    ACTION_INSTITUTIONS_FILTER,

    InstitutionAction
} from '../actions/institution';
import { RequestState } from "../enumerations/RequestState";
import { IRequestState, IFormFilter } from "./index";
import { updateFailureIndexToId } from "../helpers/formErrorHelper";

export interface IInstitutionState {
    formData: Dtos.Institution | Dtos.Institution[],
    formState: Dtos.FormState,
    formProperties: Dtos.FormProperty[],
    validationFailures: Dtos.ResponseError[];
    lookups: Dtos.Lookup[],
    loadState: IRequestState;
    saveState: IRequestState;
    updateState: IRequestState;
    createState: IRequestState;
    validateState: IRequestState;
    formFilter: IFormFilter;
    formFilteredData: Dtos.Institution[];
    formLength: number;
}

const initialState: IInstitutionState = {
    formData: undefined,
    formState: undefined,
    formProperties: undefined,
    validationFailures: undefined,
    lookups: undefined,
    loadState: {
        status: RequestState.None
    } as IRequestState,
    saveState: {
        status: RequestState.None
    } as IRequestState,
    updateState: {
        status: RequestState.None
    } as IRequestState,
    createState: {
        status: RequestState.None
    } as IRequestState,
    validateState: {
        status: RequestState.None
    } as IRequestState,
    formFilter: {
        search: "",
        showInActive: false
    },
    formFilteredData: [],
    formLength: 0
};


function loading(state: IInstitutionState, _isLoading: boolean): IInstitutionState {
    const newState: IInstitutionState = update(state, {
        loadState: {
            $set: {
                status: RequestState.Pending
            } as IRequestState
        }
    });

    return newState;
}

function loadSuccess(state: IInstitutionState,
    formData: Dtos.Institution | Dtos.Institution[],
    formState: Dtos.FormState,
    formProperties: Dtos.FormProperty[],
    lookups: Dtos.Lookup[],
    responseStatus: Dtos.ResponseStatus
): IInstitutionState {
    const newState: IInstitutionState = update(state, {
        formData: {
            $set: formData
        },
        formState: {
            $set: formState
        },
        formProperties: {
            $set: formProperties
        },
        lookups: {
            $set: lookups
        },
        loadState: {
            $set: {
                status: RequestState.Success,
                errorCode: responseStatus ? responseStatus.errorCode : undefined,
                errorMessage: responseStatus ? responseStatus.message : undefined,
                errors: responseStatus ? responseStatus.errors : undefined,
                meta: responseStatus ? responseStatus.meta : undefined
            } as IRequestState
        },
        validationFailures: {
            $set: responseStatus ? updateFailureIndexToId(responseStatus.errors, formData) : undefined
        }
    });

    return filter(newState, state.formFilter);
}

function loadFailure(state: IInstitutionState, responseStatus: Dtos.ResponseStatus): IInstitutionState {
    const newState: IInstitutionState = update(state, {
        loadState: {
            $set: {
                status: RequestState.Failure,
                errorCode: responseStatus?.errorCode,
                errorMessage: responseStatus?.message,
                errors: responseStatus?.errors
            } as IRequestState
        }
    });

    return newState;
}

function filter(state: IInstitutionState, formFilter: IFormFilter): IInstitutionState {

    const items: Dtos.Institution[] | undefined =
        state.formData instanceof Array ?
            state.formData :
            undefined;

    const formLength = items ? items.length : 0;

    const formFilteredData = items ? items.filter(p => (p.active == true || formFilter.showInActive) &&
        (formFilter.search == "" || p.display.toLowerCase().indexOf(formFilter.search.toLowerCase()) > -1)) : undefined;

    const newState: IInstitutionState = update(state, {
        formFilter: {
            $set: formFilter
        },
        formFilteredData: {
            $set: formFilteredData
        },
        formLength: {
            $set: formLength
        }
    });

    return newState;
}

function clear(state: IInstitutionState, propertiesToClear?: string[]): IInstitutionState {

    if (!propertiesToClear) {
        const newState: IInstitutionState = update(initialState, {});
        return newState;
    }

    let stateModifier: any = {};

    propertiesToClear.forEach(propertyToClear => {
        stateModifier[propertyToClear] = { $set: initialState[propertyToClear] }
    });

    const newState = update(state, stateModifier);

    return newState;
}

function saving(state: IInstitutionState, _isSaving: boolean): IInstitutionState {
    const newState: IInstitutionState = update(state, {
        saveState: {
            $set: {
                status: RequestState.Pending
            } as IRequestState
        }
    });

    return newState;
}

function saveSuccess(
    state: IInstitutionState,
    formData: Dtos.Institution | Dtos.Institution[],
    formState: Dtos.FormState,
    responseStatus: Dtos.ResponseStatus
): IInstitutionState {
    const newState: IInstitutionState = update(state, {
        formData: {
            $set: formData
        },
        formState: {
            $set: formState
        },
        saveState: {
            $set: {
                status: RequestState.Success,
                errorCode: responseStatus ? responseStatus.errorCode : undefined,
                errorMessage: responseStatus ? responseStatus.message : undefined,
                errors: responseStatus ? responseStatus.errors : undefined,
                meta: responseStatus ? responseStatus.meta : undefined
            } as IRequestState
        },
        validationFailures: {
            $set: responseStatus ? updateFailureIndexToId(responseStatus.errors, formData) : undefined
        }
    });

    return newState;
}

function saveFailure(state: IInstitutionState, responseStatus: Dtos.ResponseStatus): IInstitutionState {
    const newState: IInstitutionState = update(state, {
        saveState: {
            $set: {
                status: RequestState.Failure,
                errorCode: responseStatus?.errorCode,
                errorMessage: responseStatus?.message,
                errors: responseStatus?.errors,
                meta: responseStatus.meta
            } as IRequestState
        },
        validationFailures: {
            $set: responseStatus ? updateFailureIndexToId(responseStatus.errors, state.formData) : undefined
        }
    });

    return newState;
}

function creating(state: IInstitutionState, _isCreating: boolean): IInstitutionState {
    const newState: IInstitutionState = update(state, {
        createState: {
            $set: {
                status: RequestState.Pending
            } as IRequestState
        }
    });

    return newState;
}

function createSuccess(
    state: IInstitutionState,
    formData: Dtos.Institution,
    formState: Dtos.FormState,
    formProperties: Dtos.FormProperty[],
    lookups: Dtos.Lookup[]
): IInstitutionState {
    const newState: IInstitutionState = update(state, {
        formData: {
            $set: formData
        },
        formState: {
            $set: formState
        },
        formProperties: {
            $set: formProperties
        },
        lookups: {
            $set: lookups
        },
        createState: {
            $set: {
                status: RequestState.Success
            } as IRequestState
        }
    });

    return newState;
}

function createFailure(state: IInstitutionState, responseStatus: Dtos.ResponseStatus): IInstitutionState {
    const newState: IInstitutionState = update(state, {
        createState: {
            $set: {
                status: RequestState.Failure,
                errorCode: responseStatus?.errorCode,
                errorMessage: responseStatus?.message,
                errors: responseStatus?.errors
            } as IRequestState
        }
    });

    return newState;
}

function validating(state: IInstitutionState, _isValidating: boolean): IInstitutionState {
    const newState: IInstitutionState = update(state, {
        validateState: {
            $set: {
                status: RequestState.Pending
            } as IRequestState
        }
    });

    return newState;
}

function validateSuccess(
    state: IInstitutionState,
    formData: Dtos.Institution,
    formState: Dtos.FormState,
    responseStatus: Dtos.ResponseStatus
): IInstitutionState {
    const newState: IInstitutionState = update(state, {
        formData: {
            $set: formData
        },
        formState: {
            $set: formState
        },
        validateState: {
            $set: {
                status: RequestState.Success,
                errorCode: responseStatus ? responseStatus.errorCode : undefined,
                errorMessage: responseStatus ? responseStatus.message : undefined,
                errors: responseStatus ? responseStatus.errors : undefined,
                meta: responseStatus ? responseStatus.meta : undefined
            } as IRequestState
        },
        validationFailures: {
            $set: responseStatus ? updateFailureIndexToId(responseStatus.errors, formData) : undefined
        }
    });

    return newState;
}

function validateFailure(state: IInstitutionState, responseStatus: Dtos.ResponseStatus): IInstitutionState {
    const newState: IInstitutionState = update(state, {
        validateState: {
            $set: {
                status: RequestState.Failure,
                errorCode: responseStatus?.errorCode,
                errorMessage: responseStatus?.message,
                errors: responseStatus?.errors,
                meta: responseStatus.meta
            } as IRequestState
        },
        validationFailures: {
            $set: responseStatus ? updateFailureIndexToId(responseStatus.errors, state.formData) : undefined
        }
    });

    return newState;
}

const InstitutionReducer: Reducer<IInstitutionState> = (state: IInstitutionState = initialState, action: InstitutionAction) => {
    switch (action.type) {
        case ACTION_INSTITUTIONS_LOAD:
        case ACTION_INSTITUTIONS_LOAD_BY_ID:
            return loading(state, true);
        case ACTION_INSTITUTIONS_LOAD_SUCCESS:
            return loadSuccess(state, action.formData, action.formState, action.formProperties, action.lookups, action.responseStatus);
        case ACTION_INSTITUTIONS_LOAD_FAILURE:
            return loadFailure(state, action.responseStatus);
        case ACTION_INSTITUTIONS_CLEAR:
            return clear(state, action.propertiesToClear);
        case ACTION_INSTITUTIONS_SAVE:
            return saving(state, true);
        case ACTION_INSTITUTIONS_SAVE_SUCCESS:
            return saveSuccess(state, action.formData, action.formState, action.responseStatus);
        case ACTION_INSTITUTIONS_SAVE_FAILURE:
            return saveFailure(state, action.responseStatus);
        case ACTION_INSTITUTIONS_CREATE:
            return creating(state, true);
        case ACTION_INSTITUTIONS_CREATE_SUCCESS:
            return createSuccess(state, action.formData, action.formState, action.formProperties, action.lookups);
        case ACTION_INSTITUTIONS_CREATE_FAILURE:
            return createFailure(state, action.responseStatus);
        case ACTION_INSTITUTIONS_VALIDATE:
            return validating(state, true);
        case ACTION_INSTITUTIONS_VALIDATE_SUCCESS:
            return validateSuccess(state, action.formData, action.formState, action.responseStatus);
        case ACTION_INSTITUTIONS_VALIDATE_FAILURE:
            return validateFailure(state, action.responseStatus);
        case ACTION_INSTITUTIONS_FILTER:
            return filter(state, action.formFilter);
    }

    return state;
}

export default InstitutionReducer;